// Staff/coach-facing track & field marks card on the athlete detail page.
// Read-only display grouped by event; verified marks show a blue seal. Staff /
// partners (perms.canVerify) get a toggle seal, coaches see it read-only —
// same rule as AccoladesCard. Rendered only for track athletes.
import React from 'react';
import SealButton from './SealButton';
import useMarkVerify from './useMarkVerify';
import { TIMING_LABEL, VENUE_LABEL } from '../../lib/eventMarks';

function formatWind(m) {
  if (m.wind_reading == null) return null;
  const n = Number(m.wind_reading);
  const sign = n > 0 ? '+' : '';
  const legal = m.wind_legal === false ? ' (illegal)' : '';
  return `${sign}${n.toFixed(1)} m/s${legal}`;
}

// Group marks by event_key, ordered to match the catalog event order.
function groupByEvent(marks, positions) {
  const order = new Map(positions.map((p, i) => [p.slug, i]));
  const label = new Map(positions.map(p => [p.slug, p.label]));
  const byEvent = new Map();
  for (const m of marks) {
    if (!byEvent.has(m.event_key)) byEvent.set(m.event_key, []);
    byEvent.get(m.event_key).push(m);
  }
  return [...byEvent.entries()]
    .sort((a, b) => (order.get(a[0]) ?? 999) - (order.get(b[0]) ?? 999))
    .map(([key, rows]) => ({ key, label: label.get(key) || key, rows }));
}

export default function EventMarksCard({ userId, marks, setMarks, canVerify, apiUrl, getConfig, positions }) {
  const { toggleVerify, busyId } = useMarkVerify({ userId, apiUrl, getConfig, setMarks });
  if (!marks || marks.length === 0) return null;

  const groups = groupByEvent(marks, positions);

  return (
    <div className="ae-card ae-card--pad">
      <div className="ae-card__head">
        <h3 className="ae-card__title">Event Marks</h3>
        {canVerify && <span className="ae-card__hint">Click a seal to verify</span>}
      </div>
      <div className="ae-marks">
        {groups.map(g => (
          <div key={g.key} className="ae-mark-group">
            <div className="ae-mark-group__head">{g.label}</div>
            {g.rows.map(m => {
              const wind = formatWind(m);
              return (
                <div key={m.id} className="ae-accolade">
                  <div className="ae-accolade__body">
                    <div className="ae-accolade__title">
                      {m.mark_value}
                      {m.is_pr && <span className="ae-mark-badge ae-mark-badge--pr">PR</span>}
                      {m.is_sb && <span className="ae-mark-badge ae-mark-badge--sb">SB</span>}
                    </div>
                    <div className="ae-accolade__meta">
                      {m.meet_name && <span className="ae-accolade__src">{m.meet_name}</span>}
                      {m.meet_date && <span className="ae-accolade__src">{m.meet_date}</span>}
                      {wind && <span className="ae-accolade__src">{wind}</span>}
                      {m.timing_type && <span className="accolade-chip">{TIMING_LABEL[m.timing_type] || m.timing_type}</span>}
                      {m.venue_type && <span className="accolade-chip">{VENUE_LABEL[m.venue_type] || m.venue_type}</span>}
                      {m.season_year && <span className="ae-accolade__src">{m.season_year}</span>}
                    </div>
                  </div>
                  {canVerify ? (
                    <SealButton
                      className="ae-accolade__seal"
                      state={m.verified ? 'verified' : 'unverified'}
                      title={m.verified ? 'Verified — click to unverify' : 'Not verified — click to verify'}
                      disabled={busyId === m.id}
                      onToggle={() => toggleVerify(m)}
                    />
                  ) : (
                    m.verified && (
                      <SealButton className="ae-accolade__seal" state="verified" title="Verified" disabled onToggle={() => {}} />
                    )
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
